import { useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { returnItemToQueueApi, type AdminQueueItem } from "@/portals/admin/api/adminReviewQueueApi";
import { adminKeys, invalidateReviewQueue, reviewQueueItemQuery } from "@/portals/admin/api/adminQueries";
import { AttachedViaTag, ReviewStatusBadge } from "@/features/clinician/review-queue/components/reviewBadges";
import {
  PatientIdentity,
  ReviewItemLayout,
  ReviewItemScreenMessage,
  ViewOnlyActionPanel,
  formatDate,
} from "@/features/clinician/review-queue/components/ReviewItemLayout";
import { ReviewHistory } from "@/features/clinician/review-queue/components/ReviewHistory";
import { ASSIGN_MESSAGES, AssignClinicianModal } from "./AssignClinicianModal";

export const ADMIN_ITEM_MESSAGES = {
  note: "View only. Assign this item to a clinician to have it reviewed.",
  heldNote: "View only. Reassign it to another clinician, or return it to the shared queue.",
  closedNote: "This item is closed.",
  notFound: ASSIGN_MESSAGES.itemGone,
  notAssignable: ASSIGN_MESSAGES.notAssignable,
  loadFailed: "Couldn't load this item. Please try again.",
  returned: "Item returned to the shared queue.",
  notHeld: "This item isn't with a clinician anymore.",
};

const OUTLINE =
  "!h-10 !w-full !rounded-lg !border-[#E5E7EB] !text-sm !font-semibold !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]";

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : `${formatDate(value)}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

interface AdminQueueItemViewProps {
  itemId: string;
  onBack: () => void;
  /** Assigned/reassigned or returned — back to where the admin came from. */
  onAssigned: () => void;
  /** Where Back goes, for its label. */
  backLabel?: string;
}

/** An admin's read-only view of any task — in the queue (Assign), with a clinician
 *  (Reassign / Return to queue) or closed — with the whole conversation. */
export function AdminQueueItemView({ itemId, onBack, onAssigned, backLabel = "Review Queue" }: AdminQueueItemViewProps) {
  const [assigning, setAssigning] = useState(false);
  const [returning, setReturning] = useState(false);
  const client = useQueryClient();

  // Cached per item: reopening it shows it at once, then revalidates quietly.
  // Live: claims, assignments, patient messages and decisions all refresh the
  // view (useAdminLiveQueries).
  const query = useQuery(reviewQueueItemQuery(itemId));
  const refresh = () => void query.refetch();

  // The item moved: drop it from the queue list; every queue view revalidates.
  const forgetItem = () => {
    client.setQueryData<AdminQueueItem[]>(adminKeys.reviewQueueList, (list) => list?.filter((i) => i.id !== itemId));
    void invalidateReviewQueue(client);
  };

  const back = { backLabel, onBack };
  if (!query.data) {
    if (!query.isError) return <ReviewItemScreenMessage loading {...back} />;
    const code = (query.error as { code?: string } | null)?.code;
    return (
      <ReviewItemScreenMessage
        icon={code === "REVIEW_ITEM_NOT_FOUND" ? "lucide:file-x" : "lucide:wifi-off"}
        message={code === "REVIEW_ITEM_NOT_FOUND" ? ADMIN_ITEM_MESSAGES.notFound : ADMIN_ITEM_MESSAGES.loadFailed}
        {...back}
      />
    );
  }

  const item = query.data;
  const state = item.state ?? "IN_QUEUE";

  const returnToQueue = async () => {
    setReturning(true);
    const { error } = await returnItemToQueueApi(itemId);
    setReturning(false);
    if (!error) {
      toastSuccess(ADMIN_ITEM_MESSAGES.returned);
      void invalidateReviewQueue(client);
    } else if (error.code === "ITEM_NOT_HELD") {
      toastError(ADMIN_ITEM_MESSAGES.notHeld);
      refresh();
    } else {
      toastError(error.message ?? "Unable to return the item.");
    }
  };

  const holderCard = item.clinician ? (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm" data-testid="admin-holder-card">
      <p className="text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">
        {state === "CLOSED" ? "Last clinician" : "With clinician"}
      </p>
      <p className="mt-1 text-sm font-extrabold text-[#0A2947]">{item.clinician.name}</p>
      <p className="text-xs text-[#6B7280]">{item.clinician.email}</p>
      <dl className="!mb-0 mt-3 grid gap-1.5 text-xs">
        <div className="flex justify-between gap-3">
          <dt className="text-[#9CA3AF]">{item.attachedVia === "ASSIGNED_BY_ADMIN" ? "Assigned" : "Claimed"}</dt>
          <dd className="!m-0 font-semibold text-[#0A2947]">{formatDateTime(item.attachedAt)}</dd>
        </div>
        {item.resolvedAt && (
          <div className="flex justify-between gap-3">
            <dt className="text-[#9CA3AF]">Closed</dt>
            <dd className="!m-0 font-semibold text-[#0A2947]">{formatDateTime(item.resolvedAt)}</dd>
          </div>
        )}
      </dl>
    </div>
  ) : null;

  const aside =
    state === "IN_QUEUE" ? (
      <ViewOnlyActionPanel
        testId="admin-assign-panel"
        note={ADMIN_ITEM_MESSAGES.note}
        actionLabel="Assign to a clinician"
        onAction={() => setAssigning(true)}
      />
    ) : state === "WITH_CLINICIAN" ? (
      <>
        {holderCard}
        <div className="grid gap-2 rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm" data-testid="admin-held-actions">
          <div className="flex items-start gap-2.5">
            <Icon icon="lucide:eye" className="mt-0.5 h-4 w-4 shrink-0 text-[#6B7280]" />
            <p className="text-sm text-[#4B5563]">{ADMIN_ITEM_MESSAGES.heldNote}</p>
          </div>
          <Button
            onClick={() => setAssigning(true)}
            className="!mt-2 !h-10 !w-full !rounded-lg !border-transparent !bg-[#E4002B]/50 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
          >
            Reassign
          </Button>
          <Button variant="outline" busy={returning} onClick={() => void returnToQueue()} className={OUTLINE}>
            Return to queue
          </Button>
        </div>
      </>
    ) : (
      <>
        {holderCard}
        <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 text-sm text-[#4B5563] shadow-sm">
          {ADMIN_ITEM_MESSAGES.closedNote}
        </div>
      </>
    );

  return (
    <>
      <ReviewItemLayout
        item={item}
        {...back}
        identity={<PatientIdentity patient={item.patient} />}
        badges={
          <>
            <ReviewStatusBadge status={item.itemStatus} />
            {item.attachedVia && state !== "IN_QUEUE" && <AttachedViaTag via={item.attachedVia} />}
          </>
        }
        renderConversation={(isPair) => <ReviewHistory item={item} isPair={isPair} readOnly />}
        aside={aside}
      />
      <AssignClinicianModal
        itemId={assigning ? itemId : null}
        currentClinicianId={state === "WITH_CLINICIAN" ? item.clinician?.userId : null}
        onClose={() => setAssigning(false)}
        onAssigned={() => {
          setAssigning(false);
          forgetItem();
          onAssigned();
        }}
        onItemGone={() => {
          setAssigning(false);
          refresh();
        }}
      />
    </>
  );
}
